// Display-locale state for formatting helpers. i18next-free on purpose: the
// candidate app shares these helpers and never imports the recruiter i18n
// runtime, so nothing here may pull it in. Only the recruiter runtime (recruiter
// and internal apps) calls setAppLanguage / registerAppTranslator; elsewhere the
// helpers keep their English (browser-default) behaviour.
import type { ParseKeys } from 'i18next';
import {
  DAYJS_LOCALE,
  DEFAULT_INTL_LOCALE,
  isRecruiterLanguage,
} from 'recruiter-i18n/languages';

/** A key of the recruiter `core` namespace (plural keys by their base name). */
export type CoreKey = ParseKeys<'core'>;

export type AppTextValues = Record<string, string | number>;

type AppTranslator = (
  key: CoreKey,
  defaultValue: string,
  values?: AppTextValues
) => string;

let appLanguage: string | null = null;
let translator: AppTranslator | null = null;
const collators = new Map<string, Intl.Collator>();
const relativeTimeFormats = new Map<string, Intl.RelativeTimeFormat>();
const listFormats = new Map<string, Intl.ListFormat>();
const percentFormats = new Map<string, Intl.NumberFormat>();
interface NumberInputSeparators {
  decimalSeparator: string;
  thousandSeparator: string;
}
const numberSeparators = new Map<string, NumberInputSeparators>();
// The collator compareDisplayStrings last resolved, keyed by its inputs, so a
// sort does not re-derive the locale for every comparison.
let lastCollator: {
  language: string | null;
  browser: string | null;
  collator: Intl.Collator;
} | null = null;

const baseOf = (tag: string): string => {
  const [base = ''] = tag.split('-');
  return base.toLowerCase();
};

const browserLanguage = (): string | null => {
  try {
    return typeof navigator === 'undefined' ? null : navigator.language || null;
  } catch {
    // swallow-ok: An unavailable browser region uses the configured UI language region; formatting remains in the active language via intlLocaleForLanguage.
    return null;
  }
};

/**
 * The Intl locale for a UI language. A bare language (fr) prefers the
 * browser's regional variant of that same language (fr-CA, fr-BE) so number
 * and date conventions match the user's region; a regional language (nl-BE)
 * is used as is. A browser tag with no region (es) names no region, so the
 * language's default applies rather than CLDR's base-language formats.
 */
export const intlLocaleForLanguage = (
  language: string,
  browser: string | null = browserLanguage()
): string => {
  if (language.includes('-')) return language;
  if (browser?.includes('-') && baseOf(browser) === language) return browser;
  return isRecruiterLanguage(language)
    ? DEFAULT_INTL_LOCALE[language]
    : language;
};

/**
 * Records the recruiter UI language. English (or null) restores the default
 * behaviour, where displayLocale() is undefined and the browser decides.
 */
export const setAppLanguage = (language: string | null): void => {
  appLanguage = language && language !== 'en' ? language : null;
};

/** The non-English UI language, or null for English. */
export const getAppLanguage = (): string | null => appLanguage;

/**
 * Locale for `toLocale*String` / `Intl.*` display formatting. Undefined for
 * English, which keeps today's browser-default output.
 */
export const displayLocale = (): string | undefined =>
  appLanguage ? intlLocaleForLanguage(appLanguage) : undefined;

/**
 * Like displayLocale, for call sites that pinned an English locale (usually
 * 'en-US') and must keep it for English users.
 */
export const displayLocaleOr = (englishDefault: string): string =>
  displayLocale() ?? englishDefault;

/**
 * Separators for numeric inputs. English and candidate apps keep their
 * existing dot decimal separator, independent of the browser language.
 * Apply decimalSeparator by default; thousandSeparator is opt-in so fields
 * without grouping keep their existing behaviour.
 */
export const numberInputSeparators = (): NumberInputSeparators => {
  const locale = displayLocaleOr('en-US');
  let separators = numberSeparators.get(locale);
  if (!separators) {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    separators = {
      decimalSeparator:
        parts.find((part) => part.type === 'decimal')?.value ?? '.',
      thousandSeparator:
        parts.find((part) => part.type === 'group')?.value ?? ',',
    };
    numberSeparators.set(locale, separators);
  }
  return separators;
};

/** dayjs locale name for Mantine date components; undefined for English. */
export const datesLocale = (): string | undefined =>
  appLanguage && isRecruiterLanguage(appLanguage)
    ? DAYJS_LOCALE[appLanguage]
    : undefined;

const displayCollator = (): Intl.Collator => {
  const browser = browserLanguage();
  if (
    lastCollator?.language === appLanguage &&
    lastCollator.browser === browser
  ) {
    return lastCollator.collator;
  }
  const locale = displayLocaleOr('en-US');
  let collator = collators.get(locale);
  if (!collator) {
    collator = new Intl.Collator(locale, {
      sensitivity: 'base',
      numeric: true,
    });
    collators.set(locale, collator);
  }
  lastCollator = { language: appLanguage, browser, collator };
  return collator;
};

/** Locale-aware comparison for UI sorts (accents, case, embedded numbers). */
export const compareDisplayStrings = (a: string, b: string): number =>
  displayCollator().compare(a, b);

/**
 * Locale-independent code-unit order, for ids, keys and ISO dates that are
 * not shown as text (or must sort the same for every user).
 */
export const compareKeys = (a: string, b: string): number =>
  a < b ? -1 : a > b ? 1 : 0;

/** Relative time ("3 days ago", "il y a 3 jours") in the display locale. */
export const formatRelativeTime = (
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  numeric: 'always' | 'auto' = 'auto'
): string => {
  const locale = displayLocaleOr('en-US');
  const cacheKey = `${locale}|${numeric}`;
  let format = relativeTimeFormats.get(cacheKey);
  if (!format) {
    format = new Intl.RelativeTimeFormat(locale, { numeric });
    relativeTimeFormats.set(cacheKey, format);
  }
  return format.format(value, unit);
};

export interface PercentFormatOptions {
  /** Default 0. */
  minimumFractionDigits?: number;
  /** Default minimumFractionDigits (so 0: whole percents). */
  maximumFractionDigits?: number;
  /**
   * Intl locale; default the display locale. Candidate pages pass their
   * i18next language, which the recruiter display locale does not track.
   */
  locale?: string;
}

/**
 * A percentage given on the 0-100 scale (50 -> "50%" in English, "50 %" with
 * a narrow no-break space in French), rounded to whole percents unless
 * fraction digits are given. Pass `rate * 100` for a 0-1 rate.
 */
export const formatPercent = (
  value: number,
  options: PercentFormatOptions = {}
): string => {
  const minimumFractionDigits = options.minimumFractionDigits ?? 0;
  const maximumFractionDigits = Math.max(
    options.maximumFractionDigits ?? minimumFractionDigits,
    minimumFractionDigits
  );
  const locale = options.locale ?? displayLocaleOr('en-US');
  const cacheKey = `${locale}|${minimumFractionDigits}|${maximumFractionDigits}`;
  let format = percentFormats.get(cacheKey);
  if (!format) {
    format = new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits,
      maximumFractionDigits,
    });
    percentFormats.set(cacheKey, format);
  }
  const fraction = value / 100;
  const zero = format.format(0);
  // Intl writes "-0%" for a small negative that rounds to zero.
  if (fraction < 0 && format.format(-fraction) === zero) return zero;
  return format.format(fraction);
};

/**
 * "A, B and C". English keeps that form (no serial comma); other languages
 * use their own list style ("A, B et C", "A, B und C").
 */
export const formatConjunctionList = (items: readonly string[]): string => {
  const locale = displayLocale();
  if (!locale) {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }
  let format = listFormats.get(locale);
  if (!format) {
    format = new Intl.ListFormat(locale, {
      style: 'long',
      type: 'conjunction',
    });
    listFormats.set(locale, format);
  }
  return format.format(items);
};

/** Called once by the recruiter app so shared helpers can translate. */
export const registerAppTranslator = (fn: AppTranslator | null): void => {
  translator = fn;
};

const interpolate = (template: string, values?: AppTextValues): string =>
  values
    ? template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
        name in values ? String(values[name]) : match
      )
    : template;

/**
 * A `core` string for helpers outside React. Without a registered translator
 * (candidate app, tests) it returns the English default, so those keep their
 * exact current output.
 */
export const appText = (
  key: CoreKey,
  defaultValue: string,
  values?: AppTextValues
): string => {
  if (translator) {
    try {
      return translator(key, defaultValue, values);
    } catch {
      // swallow-ok: Translation failure renders the caller-supplied English default with the same interpolated values; no resource or user data is replaced.
      // Fall through to the English default.
    }
  }
  return interpolate(defaultValue, values);
};

/** Test-only: forget the language and translator. */
export const __resetAppLocaleForTests = (): void => {
  appLanguage = null;
  translator = null;
  collators.clear();
  lastCollator = null;
};
